import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Check, X, Shuffle, MessageCircle, Ban, Plus } from "lucide-react";

const STATUS = { pending: "bg-amber-50 text-amber-700", approved: "bg-green-50 text-green-700", declined: "bg-stone-100 text-stone-500" };
const itemsText = (o) => (o?.items || []).map(i => `${i.qty} ${i.label || i.food_name || i.food_id.replace(/^f_/, "").replace(/_/g, " ")}`).join(", ");

const RequestRow = ({ r, token, onDone }) => {
  const [note, setNote] = useState("");
  const [opts, setOpts] = useState(null);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);
  const h = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  useEffect(() => {
    if (r.status === "pending" && r.let_dietitian_decide)
      fetch(`${API}/staff/meal-plans/${r.plan_id}/swap-options?day=${r.day}&meal_type=${r.meal_type}`, { headers: h }).then(x => x.json()).then(d => setOpts(d.options || [])).catch(() => setOpts([]));
  }, [r]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (kind) => {
    if (kind === "approve" && r.let_dietitian_decide && !pick) { toast.error("Choose the new meal first."); return; }
    setBusy(true);
    try {
      const body = { note, option: kind === "approve" && pick ? { name: pick.name, items: pick.items.map(({ label, ...i }) => i) } : null };
      const res = await fetch(`${API}/staff/swap-requests/${r.request_id}/${kind}`, { method: "POST", headers: h, body: JSON.stringify(body) });
      const d = await res.json();
      if (res.ok) { toast.success(d.message); onDone(d.plan_id); } else toast.error(d.detail || "Failed.");
    } catch { toast.error("Connection error."); } finally { setBusy(false); }
  };

  return (
    <div className="border border-stone-200 rounded-lg p-3 space-y-2" data-testid={`swap-request-${r.request_id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Badge className={`text-[10px] capitalize ${STATUS[r.status] || ""}`}>{r.status}</Badge>
        <span className="text-xs font-semibold text-stone-800">Day {r.day} {r.day_name} · <span className="capitalize">{r.meal_type}</span></span>
        <span className="text-xs text-stone-400">{new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>
      </div>
      <p className="text-xs text-stone-600">Replace <strong>{r.current_meal}</strong> · {r.reason_label}{r.note ? ` · "${r.note}"` : ""}</p>
      {r.status === "approved" && <p className="text-xs text-green-700">Now: {r.approved_option?.name}</p>}
      {r.staff_note && r.status !== "pending" && <p className="text-xs text-stone-500">Your note: {r.staff_note}</p>}
      {r.whatsapp_message && r.patient_phone && (
        <Button size="sm" variant="outline" className="h-7 text-xs rounded-lg text-green-700 border-green-200 hover:bg-green-50" data-testid={`swap-whatsapp-${r.request_id}`}
          onClick={() => window.open(`https://wa.me/${r.patient_phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(r.whatsapp_message)}`, "_blank")}>
          <MessageCircle className="w-3.5 h-3.5 mr-1" />Tell patient on WhatsApp
        </Button>
      )}
      {r.status === "pending" && (
        <>
          {r.let_dietitian_decide ? (
            <div className="space-y-1">
              <p className="text-[11px] text-stone-500">Patient asked you to choose. Pick a meal from their ticked foods:</p>
              {!opts ? <Loader2 className="w-4 h-4 animate-spin text-[#FF7A00]" /> : opts.length ? opts.map((o, i) => (
                <button key={o.name} onClick={() => setPick(o)} data-testid={`swap-pick-${r.request_id}-${i}`} className={`w-full text-left rounded-md border px-2 py-1.5 ${pick?.name === o.name ? "border-[#FF7A00] bg-[#FFF7F0]" : "border-stone-200 hover:border-[#FF7A00]/40"}`}>
                  <p className="text-xs font-semibold text-stone-800">{o.name}</p><p className="text-[11px] text-stone-500">{itemsText(o)}</p>
                </button>
              )) : <p className="text-xs text-stone-500">No alternatives from the patient's ticked foods. Edit the meal by hand, then decline with a note.</p>}
            </div>
          ) : (
            <div className="rounded-md bg-[#FFF7F0] border border-[#FF7A00]/20 px-2 py-1.5"><p className="text-xs font-semibold text-stone-800 flex items-center gap-1"><Shuffle className="w-3 h-3 text-[#FF7A00]" />Patient chose: {r.requested_option?.name}</p><p className="text-[11px] text-stone-500">{itemsText(r.requested_option)}</p></div>
          )}
          <div className="flex gap-2 items-center">
            <Input value={note} onChange={e => setNote(e.target.value)} placeholder="Note to patient (optional)" className="h-8 text-xs rounded-lg" data-testid={`swap-note-${r.request_id}`} />
            <Button size="sm" disabled={busy} onClick={() => act("approve")} className="h-8 text-xs rounded-lg bg-green-600 hover:bg-green-700 text-white" data-testid={`swap-approve-${r.request_id}`}><Check className="w-3.5 h-3.5 mr-1" />Approve</Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => act("decline")} className="h-8 text-xs rounded-lg" data-testid={`swap-decline-${r.request_id}`}><X className="w-3.5 h-3.5 mr-1" />Decline</Button>
          </div>
        </>
      )}
    </div>
  );
};

export const PatientSwapRequests = ({ patientId, token, onApplied }) => {
  const [reqs, setReqs] = useState(null);
  const load = useCallback(() => {
    fetch(`${API}/staff/patient/${patientId}/swap-requests`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).then(d => setReqs(d.requests || [])).catch(() => setReqs([]));
  }, [patientId, token]);
  useEffect(load, [load]);
  if (!reqs?.length) return null;
  const pending = reqs.filter(r => r.status === "pending").length;
  return (
    <div className="space-y-2" data-testid="patient-swap-requests">
      <p className="text-xs font-bold text-stone-700">Meal swap requests from patient {pending > 0 && <Badge className="ml-1 bg-amber-50 text-amber-700 text-[10px]">{pending} waiting</Badge>}</p>
      {reqs.map(r => <RequestRow key={r.request_id} r={r} token={token} onDone={(pid) => { load(); onApplied?.(pid); }} />)}
    </div>
  );
};

export const PendingSwapsCard = ({ token }) => {
  const [reqs, setReqs] = useState([]);
  useEffect(() => {
    fetch(`${API}/staff/swap-requests/pending`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : { requests: [] }).then(d => setReqs(d.requests || [])).catch(() => {});
  }, [token]);
  if (!reqs.length) return null;
  const byPatient = Object.values(reqs.reduce((a, r) => { (a[r.patient_id] ||= { ...r, count: 0 }).count++; return a; }, {}));
  return (
    <Card className="p-4 rounded-xl border-[#FF7A00]/30 bg-[#FFF7F0] space-y-2" data-testid="pending-swaps-card">
      <p className="text-sm font-bold text-stone-900 flex items-center gap-2"><Shuffle className="w-4 h-4 text-[#FF7A00]" />{reqs.length} meal swap request{reqs.length > 1 ? "s" : ""} waiting for review</p>
      <div className="flex flex-wrap gap-2">
        {byPatient.map(p => (
          <Link key={p.patient_id} to={`/admin/patients/${p.patient_id}#meal-plans`} className="text-xs bg-white border border-stone-200 rounded-full px-3 py-1 hover:border-[#FF7A00]/50" data-testid={`pending-swaps-${p.patient_id}`}>{p.patient_name} ({p.count})</Link>
        ))}
      </div>
    </Card>
  );
};

export const AvoidedMeals = ({ patientId, token, refreshKey }) => {
  const [list, setList] = useState(null);
  const [name, setName] = useState("");
  const h = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const load = useCallback(() => {
    fetch(`${API}/staff/patient/${patientId}/avoided-meals`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).then(d => setList(d.avoided_meals || [])).catch(() => setList([]));
  }, [patientId, token]);
  useEffect(load, [load, refreshKey]);
  const add = async () => {
    if (!name.trim()) return;
    const r = await fetch(`${API}/staff/patient/${patientId}/avoided-meals`, { method: "POST", headers: h, body: JSON.stringify({ name }) });
    const d = await r.json();
    if (r.ok) { setName(""); load(); } else toast.error(d.detail || "Could not add.");
  };
  const remove = async (n) => {
    await fetch(`${API}/staff/patient/${patientId}/avoided-meals?name=${encodeURIComponent(n)}`, { method: "DELETE", headers: h });
    load();
  };
  if (!list) return null;
  return (
    <div className="rounded-lg border border-stone-200 p-3 space-y-2" data-testid="avoided-meals">
      <p className="text-xs font-bold text-stone-700 flex items-center gap-1"><Ban className="w-3.5 h-3.5 text-red-500" />Avoided meals <span className="font-normal text-stone-400">(left out of new plans and swap suggestions)</span></p>
      {list.length ? (
        <div className="flex flex-wrap gap-1.5">
          {list.map(m => (
            <span key={m.name} className="text-[11px] bg-red-50 text-red-800 border border-red-100 rounded-full pl-2.5 pr-1 py-0.5 flex items-center gap-1" title={m.source === "swap_request" ? "Patient asked to swap this" : "Added by staff"} data-testid={`avoided-${m.name}`}>
              {m.name}{m.source === "swap_request" && <span className="text-red-400">· patient</span>}
              <button onClick={() => remove(m.name)} className="hover:text-red-600" data-testid={`avoided-remove-${m.name}`}><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      ) : <p className="text-[11px] text-stone-400">None yet. Meals the patient asks to swap are added automatically.</p>}
      <div className="flex gap-2">
        <Input value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === "Enter" && add()} placeholder="Add a meal to avoid, e.g. Pap with moi moi" className="h-8 text-xs rounded-lg" data-testid="avoided-input" />
        <Button size="sm" variant="outline" onClick={add} className="h-8 text-xs rounded-lg" data-testid="avoided-add"><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>
      </div>
    </div>
  );
};
